import cn from 'classnames';

import { dateFormatter } from '@/utils/dateFormatter';

import type { GroupSeatData } from '../groupSubscriptionTypes';
import { isSeatBulkActionable } from './groupCard.utils';
import { type RowActionCallbacks, RowActions } from './RowActions';
import { SeatStatusBadge } from './SeatStatusBadge';

type MemberRowProps = {
  seat: GroupSeatData;
  isSelected: boolean;
  onToggle: (seatId: string) => void;
  rowCallbacks: RowActionCallbacks;
  translations?: Record<string, string>;
};

export function MemberRow({ seat, isSelected, onToggle, rowCallbacks, translations }: MemberRowProps) {
  const isActionable = isSeatBulkActionable(seat);
  const dateLabel = dateFormatter({}).format(new Date(seat.created_at));
  // Mobile-only "Added: {date}" line — desktop already shows the date in its own column.
  const addedLabel =
    translations?.['group_subscriptions.card.added_label']?.replace('{{date}}', dateLabel) ?? `Added: ${dateLabel}`;
  return (
    <div
      className={cn(
        'flex items-stretch border-b border-gray-200 last:border-b-0 md:h-16 md:items-center',
        isSelected ? 'bg-gray-50' : '',
      )}
    >
      {/* Below md: email cell flex-grows, status badge + date stack under the email.
          md+: classic 300px column with status/date in their own columns.
          items-start on mobile aligns the checkbox to the email row (top) instead
          of centering it across the 3-line stack; md+ reverts to center alignment. */}
      <div className="flex flex-1 items-start gap-3 px-4 py-3 md:h-full md:w-[300px] md:flex-none md:items-center">
        <input
          type="checkbox"
          className={cn(
            // mt-0.5 nudges the checkbox so its center aligns with the email
            // line's optical center on mobile (16px checkbox in a 20px line-
            // height row). md+ centers across the row, so the offset is dropped.
            'w-4 h-4 mt-0.5 border-gray-400 rounded shrink-0 md:mt-0',
            isSelected ? '!bg-wt-primary !text-wt-text-on-primary' : '',
            !isActionable ? 'opacity-40 cursor-not-allowed' : '',
          )}
          checked={isSelected && isActionable}
          disabled={!isActionable}
          onChange={() => isActionable && onToggle(seat.id)}
          aria-label={`Select ${seat.email ?? 'seat'}`}
        />
        {/* flex-1 + min-w-0 lets this shrink inside the 300px md+ column so the
            inner `truncate` span clips with an ellipsis. Don't add md:flex-none
            here — it forces max-content sizing and long emails overflow. */}
        <div className="flex min-w-0 flex-1 flex-col gap-1 md:block">
          {/* `truncate` clips long emails; `title` exposes the full address on hover. */}
          <span title={seat.email ?? undefined} className="block truncate text-sm font-medium text-gray-900">
            {seat.email}
          </span>
          {/* Mobile order: email → date → status badge. The badge sits at the
              bottom so its padding only butts against one neighbor (the email/date
              text above), avoiding the "sandwiched" look. */}
          <span className="text-xs text-gray-500 md:hidden">{addedLabel}</span>
          <div className="md:hidden">
            <SeatStatusBadge status={seat.status} />
          </div>
        </div>
      </div>
      <div className="hidden flex-1 items-center justify-center px-4 py-3 md:flex md:h-full">
        <SeatStatusBadge status={seat.status} />
      </div>
      <div className="hidden flex-1 items-center px-4 py-3 md:flex md:h-full">
        <span className="text-sm text-gray-900">{dateLabel}</span>
      </div>
      <div className="flex items-center px-4 py-3 md:h-full md:w-[206px]">
        <RowActions status={seat.status} callbacks={rowCallbacks} />
      </div>
    </div>
  );
}
